import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { IncomeDocumentsAnswers, PendingRemoval } from '../utils/incomeDocuments';
import { isPendingRemovalListed, withoutPendingRemovals } from '../utils/incomeDocuments';
import { useDetachRequiredDocument } from './useRequiredDocumentMutations';

type DetachedLink = Omit<Extract<PendingRemoval, { kind: 'detach' }>, 'kind'>;

/** Hides a removed income document at once, until the refetched application drops it or the detach fails */
export function usePendingDocumentRemovals(applicationId: string, answers: IncomeDocumentsAnswers | null | undefined) {
  const { mutateAsync: detachRequiredDocument } = useDetachRequiredDocument();
  const [removals, setRemovals] = useState<Array<PendingRemoval>>([]);
  const [detachingIds, setDetachingIds] = useState<Array<string>>([]);
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    setRemovals((current) => {
      const next = current.filter((removal) => isPendingRemovalListed(answers, removal));
      return next.length === current.length ? current : next;
    });
  }, [answers, removals]);

  const detach = useCallback(
    async (link: DetachedLink) => {
      const removal: PendingRemoval = { kind: 'detach', ...link };
      setRemovals((current) => [...current, removal]);
      setDetachingIds((current) => [...current, link.documentId]);
      try {
        await detachRequiredDocument({ postData: { applicationId, ...link } });
      } catch {
        setRemovals((current) => current.filter((entry) => entry !== removal));
      } finally {
        setDetachingIds((current) => current.filter((id) => id !== link.documentId));
      }
    },
    [applicationId, detachRequiredDocument],
  );

  const hideDeleted = useCallback((documentId: string) => {
    setRemovals((current) => [...current, { kind: 'delete', documentId }]);
    setDeletedIds((current) => new Set(current).add(documentId));
  }, []);

  const isDetaching = useCallback((documentId: string) => detachingIds.includes(documentId), [detachingIds]);

  // Picking the same file for the same row again cancels its pending Remove
  const cancelDetach = useCallback(
    (link: DetachedLink) =>
      setRemovals((current) =>
        current.filter(
          (removal) =>
            removal.kind !== 'detach' ||
            removal.documentId !== link.documentId ||
            removal.documentType !== link.documentType ||
            removal.incomeId !== link.incomeId,
        ),
      ),
    [],
  );

  const visibleAnswers = useMemo(() => withoutPendingRemovals(answers, removals), [answers, removals]);
  const actions = useMemo(
    () => ({ detach, hideDeleted, isDetaching, cancelDetach }),
    [detach, hideDeleted, isDetaching, cancelDetach],
  );

  return { answers: visibleAnswers, deletedIds, actions };
}

type PendingRemovalActions = ReturnType<typeof usePendingDocumentRemovals>['actions'];

export const PendingRemovalsContext = createContext<PendingRemovalActions | undefined>(undefined);

export function usePendingRemovalActions() {
  const actions = useContext(PendingRemovalsContext);
  if (!actions) {
    throw new Error('usePendingRemovalActions needs a PendingRemovalsContext provider');
  }
  return actions;
}
